import logger, { LEVEL } from '../components/logger';

let myLog;
let provider;
let providerConfig;
let atsApiEndpoint;
let atsRefreshEndpoint;
let emailHash1;
let emailHash2;
const REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

export default function initialize(name, cfg) {
	myLog = logger('TargetingProvider:' + name, cfg.verbosity);
	myLog(LEVEL.FLOW, 'Initializing');
	providerConfig = cfg.targetingProviders[name];
	providerConfig.name = name;
	provider = name;

	atsApiEndpoint = providerConfig.envelopeEndpoint || 'https://api.rlcdn.com/api/identity/v2/envelope';
	atsRefreshEndpoint = providerConfig.refreshEndpoint || 'https://api.rlcdn.com/api/identity/v2/envelope/refresh';
	
	emailHash1 = localStorage.getItem('eh_lr_1');
	emailHash2 = localStorage.getItem('eh_lr_2');

	// Check envelope status
	const envelopeStatus = checkEnvelopeStatus();
	
	// Only fetch envelope if we need a new one (expiry 15 days) or need to refresh an existing one (every 30 minutes)
	const gppConsentString = getGppStringFromCookie();
	if (gppConsentString) {
		// For new envelope, we need at least one email hash
		if (envelopeStatus.needsNewEnvelope) {
			if (emailHash1 || emailHash2) {
				myLog(LEVEL.INFO, 'Creating new envelope');
				const requestUrl = buildATSUrl(gppConsentString, false);
				fetchATSEnvelope(requestUrl);
			} else {
				myLog(LEVEL.WARN, 'No email hashes found. Cannot create new envelope.');
			}
		} else if (envelopeStatus.needsRefresh) {
			// For refresh, we don't need to check for email hashes as we use the existing envelope
			myLog(LEVEL.INFO, 'Refreshing existing envelope');
			const requestUrl = buildATSUrl(gppConsentString, true);
			fetchATSEnvelope(requestUrl);
		} else {
			myLog(LEVEL.INFO, 'Valid LiveRamp envelope exists. No need to fetch or refresh.');
		}
	} else {
		myLog(LEVEL.WARN, 'No GPP consent string found. Skipping LiveRamp envelope fetch.');
	}

	window.ats = {};
	window.ats.retrieveEnvelope = retrieveEnvelope;

	return {
		attachUnit,
		run
	};
}

/**
 * Fetch the envelope from ATS API endpoint.
 */
function fetchATSEnvelope(requestUrl) {
	const xhr = new XMLHttpRequest();

	xhr.open('GET', requestUrl, true);
	xhr.setRequestHeader('Accept', 'application/json');

	xhr.onreadystatechange = function () {
		if (xhr.readyState === 4) {
			if (xhr.status === 200) {
				myLog(LEVEL.INFO, 'Retrieved identity envelopes from LiveRamp.');
				storeEnvelope(xhr.responseText);
				storeEnvelopeTTL(); // To avoid passing expired envelopes that cannot be decrypted by the LiveRamp SideCar implementation of Ad Server side.
			} else {
				myLog(LEVEL.ERROR, 'Error while receiving identity envelope from Liveramp.', xhr.status, xhr.statusText);
			}
		}
	};

	xhr.send();
}

/**
 * Build the request url based on whether it's a refresh request or not.
 */
function buildATSUrl(gppConsentString, refreshExisting) {
	// Common parameters for both refresh and new envelope requests
	const baseParams = {
		pid: parseInt(providerConfig.placementId, 10) || 2101,
		gpp_sid: 7,
		gpp: gppConsentString,
		atype: 2
	};
	
	const existingEnvelope = retrieveEnvelopeFromLocalStorage();
	let endpoint = atsApiEndpoint;
	let queryParams = [];
	
	// Build the query parameter string based on whether this is a refresh request or a new envelope request
	if (refreshExisting && existingEnvelope) {
		endpoint = atsRefreshEndpoint;
		baseParams.it = 19;
		baseParams.iv = existingEnvelope;
		
		// Convert params object to query string
		queryParams = Object.keys(baseParams)
			.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(baseParams[key])}`);
	} else {
		// Add the base parameters to the query
		queryParams.push(`pid=${encodeURIComponent(baseParams.pid)}`);
		
		// Add email hashes
		if (emailHash1) {
			queryParams.push(`it=4&iv=${encodeURIComponent(emailHash1)}`);
		}
		
		if (emailHash2) {
			queryParams.push(`it=4&iv=${encodeURIComponent(emailHash2)}`);
		}
		
		// Add remaining parameters
		queryParams.push(`gpp_sid=${encodeURIComponent(baseParams.gpp_sid)}`);
		queryParams.push(`gpp=${encodeURIComponent(baseParams.gpp)}`);
		queryParams.push(`atype=${encodeURIComponent(baseParams.atype)}`);
	}
	
	return `${endpoint}?${queryParams.join('&')}`;
}

/**
 * Store the envelope TTL in a cookie.
 */
function storeEnvelopeTTL() {
	// Set expiration time for 15 days from now
	let expirationTime = 1296000000;
	expirationTime += +new Date();

	// Set a cookie to track envelope expiration in 15 days
	document.cookie = '_lr_env_exp=' + expirationTime;

	// Set localStorage to track envelope expiration for 15 days
	localStorage.setItem('_lr_env_exp', expirationTime);
	
	// Set refresh due time (current time + refresh interval)
	const refreshDueTime = Date.now() + REFRESH_INTERVAL;
	localStorage.setItem('_lr_env_refresh_due', refreshDueTime);
}

/**
 * Store the PAIR ID TTL (15 days as per LiveRamp documentation).
 */
function storePairIdTTL() {
	let expirationTime = 1296000000;
	expirationTime += +new Date();

	document.cookie = '_lr_pairId_exp=' + expirationTime;
	localStorage.setItem('_lr_pairId_exp', expirationTime);
}

/**
 * Checks the envelope status to determine if we need to fetch a new envelope,
 * refresh an existing one, or if the current one is still valid.
 * @returns {Object} Status object with needsNewEnvelope and needsRefresh flags
 */
function checkEnvelopeStatus() {
	const expTimestamp = localStorage.getItem('_lr_env_exp');
	const refreshDueTimestamp = localStorage.getItem('_lr_env_refresh_due');
	const existingEnvelope = localStorage.getItem('_lr_env');
	const currentTime = Date.now();
	
	// If envelope is expired, clear everything and get a new one
	if (expTimestamp && parseInt(expTimestamp, 10) < currentTime) {
		myLog(LEVEL.WARN, 'LiveRamp envelope has expired. Clearing data.');
		clearLiveramp();
		return {
			needsNewEnvelope: true,
			needsRefresh: false
		};
	}
	
	// If no timestamp or envelope, we need a new envelope
	if (!expTimestamp || !existingEnvelope) {
		return {
			needsNewEnvelope: true,
			needsRefresh: false
		};
	}
	
	// Check if refresh is due based on the refresh due timestamp
	const needsRefresh = refreshDueTimestamp && currentTime > parseInt(refreshDueTimestamp, 10);
	
	return {
		needsNewEnvelope: false,
		needsRefresh
	};
}

/**
 * Clears all LiveRamp data from cookies and localStorage
 */
function clearLiveramp() {
	// Clear cookies
	document.cookie = '_lr_env_exp=; Max-Age=-99999999; Path=/;';
	document.cookie = '_lr_env=; Max-Age=-99999999; Path=/;';
	document.cookie = '_lr_pairId=; Max-Age=-99999999; Path=/;';
	document.cookie = '_lr_pairId_exp=; Max-Age=-99999999; Path=/;';

	// Clear localStorage items
	localStorage.removeItem('_lr_env_exp');
	localStorage.removeItem('_lr_env');
	localStorage.removeItem('_lr_env_refresh_due');
	localStorage.removeItem('_lr_pairId');
	localStorage.removeItem('_lr_pairId_exp');

	myLog(LEVEL.INFO, 'LiveRamp data cleared from cookies and localStorage.');
}

/**
 * Retrieves the IAB Global Privacy Platform string from our cookie and returns it, otherwise returns `null`
 * @returns {string|null} The Global Privacy Platform string from our `gpp` cookie or `null`
 */
function getGppStringFromCookie() {
	const gppCookieVal = document.cookie.match(/(?:^|;)\s*gpp=((?:[^;]|$)*)/);
	if (gppCookieVal) {
		return gppCookieVal[1];
	}
	return null;
}

/**
 * Sets a cookie with the specified attributes.
 */
function setCookie(name, value) {
	const maxAge = 60 * 60 * 24 * 15; // 15 days
	const path = '/';
	const secure = true;
	const sameSite = 'Lax';

	let cookieStr = `${name}=${value}; Max-Age=${maxAge}; Path=${path}; `;
	if (secure) {
		cookieStr += 'Secure; ';
	}
	cookieStr += `SameSite=${sameSite}`;
	document.cookie = cookieStr;
}

/**
 * Parses a JSON string response, extracts the envelope value from the envelope with source "envelopeLiveramp",
 * wraps it in an object with key "envelope", encodes it in Base64, and stores it in a cookie named "_lr_env".
 * Also processes Google PAIR IDs if present.
 */
function storeEnvelope(envelopesString) {
	let envelopesObj;
	try {
		envelopesObj = JSON.parse(envelopesString);
	} catch (error) {
		myLog(LEVEL.ERROR, 'Failed to parse envelopes JSON:', error);
		return;
	}

	if (!envelopesObj || !Array.isArray(envelopesObj.envelopes)) {
		myLog(LEVEL.WARN, 'No envelopes present.');
		return;
	}

	// Handle Main Identity/ATS Envelope by LiveRamp
	const selectedEnvelope = envelopesObj.envelopes.find(env => env.source === 'envelopeLiveramp');
	if (selectedEnvelope) {
		const envelopeObj = { envelope: selectedEnvelope.value };
		const envelopeJson = JSON.stringify(envelopeObj);
		const encodedEnvelope = btoa(envelopeJson);

		setCookie('_lr_env', encodeURIComponent(encodedEnvelope));
		localStorage.setItem('_lr_env', encodeURIComponent(encodedEnvelope));
		myLog(LEVEL.INFO, 'Stored main LiveRamp envelope.');
	} else {
		myLog(LEVEL.WARN, 'No envelope with source \'envelopeLiveramp\' found.');
	}

	// Handle PAIR IDs
	const pairIdsEnvelope = envelopesObj.envelopes.find(env => env.source === 'pairIds');
	if (pairIdsEnvelope && pairIdsEnvelope.value) {
		storePairId(pairIdsEnvelope.value);
	} else {
		myLog(LEVEL.INFO, 'No PAIR IDs found in the response.');
	}
}

/**
 * Processes and stores Google PAIR ID according to LiveRamp documentation.
 * @param {string} base64PairIds - Base64 encoded PAIR IDs from the API response
 */
function storePairId(base64PairIds) {
	try {
		// This processing is a bit convoluted but it's what LiveRamp documentation says at https://developers.liveramp.com/authenticatedtraffic-api/docs/google-pair
		// Decode the Base64 val
		const decodedPairIds = atob(base64PairIds);
		const pairIdsArray = JSON.parse(decodedPairIds);

		// Wrap in envelpe object structure
		const pairIdObj = { envelope: pairIdsArray };

		// Encode to Base64
		const pairIdJson = JSON.stringify(pairIdObj);
		const encodedPairId = btoa(pairIdJson);

		// Store in cookie and LS
		setCookie('_lr_pairId', encodeURIComponent(encodedPairId));
		localStorage.setItem('_lr_pairId', encodeURIComponent(encodedPairId));

		// Store pair id TTL
		storePairIdTTL();

		myLog(LEVEL.INFO, 'Stored LiveRamp PAIR ID in localStorage and cookie.');
	} catch (error) {
		myLog(LEVEL.ERROR, 'Failed to process PAIR ID:', error);
	}
}

/**
 * Retrieves the envelope value stored in localStorage.
 * Decodes the Base64-encoded value and returns the parsed JSON object.
 * @returns {string|null} The envelope value if found, otherwise null.
 */
function retrieveEnvelopeFromLocalStorage() {
	let encodedEnvelope = localStorage.getItem('_lr_env');
	if (!encodedEnvelope) {
		myLog(LEVEL.WARN, 'No envelope found in localStorage.');
		return null;
	}
	try {
		const envelopeJson = atob(decodeURIComponent(encodedEnvelope)); // Decode Base64
		const envelopeObj = JSON.parse(envelopeJson); // Parse JSON
		return envelopeObj.envelope || null;
	} catch (error) {
		myLog(LEVEL.ERROR, 'Failed to decode or parse envelope: ', error);
		return null;
	}
}

/**
 * Retrieves the envelope value stored in cookie, creates an envelope object and passed it to the callback method.
 * This method is essentially used by Prebid.js code to retrieve and pass the ID further along.
 */
function retrieveEnvelope(callback) {
	let env;
	let result;
	
	try {
		env = decodeURIComponent(document.cookie.match('(^|;) *_lr_env=([^;]*)')[2]);
		env = {
			envelope: JSON.parse(atob(env)).envelope
		};
		result = JSON.stringify(env);
	} catch (error) {
		myLog(LEVEL.WARN, 'Failed to retrieve envelope from cookie:', error);
		result = undefined;
	}
	
	if (callback) {
		return callback(result);
	}
	
	return result;
}

function attachUnit() {
	// Implementation not needed for this provider
}

function run(request) {
	request.handleProviderFinished(provider);
}
